import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Phone, FileText, Clock, MessageCircle, Mail, Check } from 'lucide-react';
import { saasProducts } from '../../data/mock';

const iconMap = {
  Phone,
  FileText,
  Clock,
  MessageCircle,
  Mail
};

const ProductsSection = () => {
  const [activeProduct, setActiveProduct] = useState(0);

  return (
    <section className="py-24 bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#0f0f1a] text-white relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full bg-[#B8336A]/10 blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 rounded-full bg-[#2D6A6A]/10 blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-4 relative z-10">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="inline-block px-4 py-2 rounded-full bg-white/10 text-sm font-medium text-[#00d4ff] mb-4">
            Our SaaS Products
          </span>
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            We Deliver the <span className="bg-gradient-to-r from-[#B8336A] to-[#00d4ff] bg-clip-text text-transparent">Best Products</span> Tailored to Your Needs
          </h2>
          <p className="text-gray-300 text-lg">
            These products help businesses automate operations, manage communication, and improve efficiency.
          </p>
        </div>

        {/* Products Tabs */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {saasProducts.map((product, index) => {
            const Icon = iconMap[product.icon];
            return (
              <button
                key={product.id}
                onClick={() => setActiveProduct(index)}
                className={`flex items-center gap-2 px-5 py-3 rounded-full transition-all duration-300 ${
                  activeProduct === index
                    ? 'bg-gradient-to-r from-[#B8336A] to-[#2D6A6A] text-white shadow-lg shadow-[#B8336A]/30'
                    : 'bg-white/10 text-gray-300 hover:bg-white/20'
                }`}
              >
                {Icon && <Icon size={18} />}
                <span className="text-sm font-medium">{product.name}</span>
              </button>
            );
          })}
        </div>

        {/* Active Product Display */}
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Product Info */}
          <div className="space-y-6">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#B8336A]/20 border border-[#B8336A]/30">
              {(() => {
                const Icon = iconMap[saasProducts[activeProduct].icon];
                return Icon ? <Icon size={18} className="text-[#B8336A]" /> : null;
              })()}
              <span className="text-sm font-medium text-[#B8336A]">SaaS Platform</span>
            </div>
            
            <h3 className="text-3xl font-bold">{saasProducts[activeProduct].name}</h3>
            <p className="text-gray-300 text-lg leading-relaxed">
              {saasProducts[activeProduct].description}
            </p>

            {/* Features */}
            <ul className="space-y-3">
              {saasProducts[activeProduct].features.map((feature, index) => (
                <li key={index} className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full bg-gradient-to-r from-[#B8336A] to-[#2D6A6A] flex items-center justify-center">
                    <Check size={14} />
                  </div>
                  <span className="text-gray-200">{feature}</span>
                </li>
              ))}
            </ul>

            <Link
              to="/products"
              className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-[#B8336A] to-[#2D6A6A] text-white rounded-full font-semibold hover:shadow-lg hover:shadow-[#B8336A]/30 transition-all duration-300 hover:-translate-y-1"
            >
              Learn More
              <ArrowRight size={18} />
            </Link>
          </div>

          {/* Product Visual */}
          <div className="relative">
            <div className="relative bg-gradient-to-br from-white/10 to-white/5 rounded-3xl p-8 backdrop-blur-sm border border-white/10">
              <div className="aspect-video rounded-2xl bg-gradient-to-br from-[#B8336A]/20 to-[#2D6A6A]/20 flex items-center justify-center">
                {(() => {
                  const Icon = iconMap[saasProducts[activeProduct].icon];
                  return Icon ? <Icon size={80} className="text-white/50" /> : null;
                })()}
              </div>
              
              {/* Floating Stats */}
              <div className="absolute -top-4 -right-4 bg-white rounded-2xl p-4 shadow-xl">
                <div className="text-2xl font-bold text-[#B8336A]">99.9%</div>
                <div className="text-xs text-gray-500">Uptime</div>
              </div>
              
              <div className="absolute -bottom-4 -left-4 bg-white rounded-2xl p-4 shadow-xl">
                <div className="text-2xl font-bold text-[#2D6A6A]">24/7</div>
                <div className="text-xs text-gray-500">Support</div>
              </div>
            </div>
          </div>
        </div>

        {/* View All Products */}
        <div className="text-center mt-16">
          <Link
            to="/products"
            className="inline-flex items-center gap-2 text-[#00d4ff] hover:text-white transition-colors font-medium"
          >
            View All Products
            <ArrowRight size={18} />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default ProductsSection;
